/*!
  Pioneer WFRIA MenuItemGroup v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["MenuItemGroup"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("MenuItemGroup") < 0) {
    window.__PIONEER_GLOBAL__.components.push("MenuItemGroup");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "expandable", "className", "label", "name", "aria-label", "onExpandedChange", "expanded", "onClick", "alwaysRenderChildren", "wrap", "withCheckbox", "isChecked", "isIndeterminate", "leftIcon", "groupChildrenInsideLI", "groupElement", "groupElementProps", "menuItemGroupStyles"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import Checkbox from "../Checkbox/Checkbox";
import { KEYBOARD_KEYS, MENUITEMGROUP } from "../common/constants";
import { useControlOrUnControl, useUniqueId } from "../common/hooks";
import { requiredIf, validIcon } from "../common/proptypes";
import KeyboardNavigatorItem from "../private/KeyboardNavigator/KeyboardNavigatorItem";
import extractTheme from "./extractTheme";
/**
 * MenuItemGroup is a React component that contains MenuItem children
 * and has the ability to collapse and expand.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function MenuItemGroup(_ref) {
  let {
    children,
    expandable,
    className,
    label,
    name,
    // eslint-disable-line no-unused-vars
    "aria-label": ariaLabel,
    onExpandedChange,
    // eslint-disable-line no-unused-vars
    expanded,
    // eslint-disable-line no-unused-vars
    onClick,
    // eslint-disable-line no-unused-vars
    alwaysRenderChildren,
    // eslint-disable-line no-unused-vars
    wrap,
    withCheckbox,
    isChecked,
    isIndeterminate,
    leftIcon,
    groupChildrenInsideLI,
    groupElement: Element,
    groupElementProps,
    menuItemGroupStyles
  } = _ref,
      elementProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot
  } = extractTheme({
    theme,
    wrap,
    menuItemGroupStyles
  });
  const defaultExpanded = expanded || false;
  const [getExpanded, setExpanded] = useControlOrUnControl({
    unControlledPropValue: defaultExpanded,
    controlledPropValue: expanded,
    changeHandler: onExpandedChange
  });

  const handleExpandedClick = e => {
    if (expandable) {
      const isExpanded = getExpanded();
      setExpanded(!isExpanded);
    }

    if (onClick) {
      onClick(e);
    }
  };
  /**
   * Calls the onClick callback in the case of a SPACE or ENTER keyboard
   * input on a MenuItemGroup list item with a checkbox such as in the
   * case of MultiSelect.
   */


  const handleItemGroupKeyDown = e => {
    if (withCheckbox) {
      const {
        key
      } = e;
      const validKeyPressed = key === KEYBOARD_KEYS.SPACE || key === KEYBOARD_KEYS.SPACE_IE11 || key === KEYBOARD_KEYS.ENTER;

      if (validKeyPressed) {
        e.preventDefault();
        onClick(e);
      }
    }
  };

  const {
    menuItemGroupIcon,
    menuGroupButtonStyles,
    menuItemGroupLabel
  } = menuItemGroupStyles;
  const showChildren = expandable && getExpanded() || !expandable;
  const renderChildren = showChildren || alwaysRenderChildren;
  const containerClasses = clsx("pnr-menu-item-group", {
    "pnr-menu-item-group--expanded": showChildren,
    "pnr-menu-item-group--wrap": wrap
  }, className);
  const itemGroupClasses = clsx("pnr-menu-item-group__item", "wf-u-text-truncate");
  const menuItemGroupCheckboxName = useUniqueId("menuItemGroupCheckbox-");
  const grandChildItem = withCheckbox ? _jsx(Checkbox, {
    decorative: true,
    checked: isChecked,
    indeterminate: isIndeterminate,
    label: label,
    name: menuItemGroupCheckboxName,
    "aria-label": ariaLabel
  }) : label;
  return _jsxs(React.Fragment, {
    children: [_jsx(ListWrapper, {
      checkbox: withCheckbox,
      children: _jsxs("li", _objectSpread(_objectSpread({
        onClick: handleExpandedClick,
        onKeyDown: handleItemGroupKeyDown,
        className: containerClasses,
        css: cssRoot
      }, elementProps), {}, {
        children: [groupChildrenInsideLI && _jsx(KeyboardNavigatorItem, {
          children: _jsxs(Element, _objectSpread(_objectSpread({
            className: itemGroupClasses,
            css: menuGroupButtonStyles
          }, groupElementProps), {}, {
            children: [_jsx("span", {
              css: menuItemGroupIcon,
              className: "pnr-menu-item-group__icon",
              children: leftIcon
            }), _jsx("span", {
              css: menuItemGroupLabel,
              className: "pnr-menu-item-group__label",
              children: label
            })]
          }))
        }), !groupChildrenInsideLI && grandChildItem, renderChildren && groupChildrenInsideLI && children]
      }))
    }), renderChildren && !groupChildrenInsideLI && children]
  });
}

MenuItemGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Prop to decide if children should be rendered to the DOM when not expanded. */
  alwaysRenderChildren: PropTypes.bool,

  /** `MenuItem`s to be displayed in the `MenuItemGroup` components. */
  children: PropTypes.node,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /**
   * Flag indicating whether or not the children of the group can be expanded/collapsed.
   */
  expandable: PropTypes.bool,

  /**
   * Flag indicating whether or not the MenuItem should be expanded when rendering.
   */
  expanded: PropTypes.bool,

  /**
   * Determines whether the children are render inside the <li> or right after it.
   */
  groupChildrenInsideLI: PropTypes.bool,

  /**
   * Element to wrap the label and icon.
   */
  groupElement: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),

  /**
   * Props for Element wrapping the label and icon.
   */
  groupElementProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Private prop used to set the checked state of the Checkbox along
   * with MenuItem ussed in MultiSelect to render a MenuItem with Checkbox.
   */
  isChecked: PropTypes.bool,

  /**
   * Private prop used to set the indeterminate state of the Checkbox along
   * with MenuItem used in MultiSelect to render a MenuItem with Checkbox.
   */
  isIndeterminate: PropTypes.bool,

  /** Text to be shown in the MenuItemGroup. */
  label: PropTypes.node,

  /** A React component of type Icon to be placed on the left. See Iconography section. */
  leftIcon: validIcon,

  /**
   * Prop for Element wrapping styles.
   */
  menuItemGroupStyles: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Pass through prop for Checkbox.
   *
   */
  name: requiredIf(PropTypes.string, (props, propName) => !props[propName] && props.withCheckbox, "if `withCheckbox` is true"),

  /** onClick handler. */
  onClick: PropTypes.func,

  /**
   * Callback triggered when the `MenuItemGroup` expands or collapses.
   */
  onExpandedChange: PropTypes.func,

  /**
   * Private prop used to render a Checkbox along with MenuItem.
   * Used in MultiSelect to render a MenuItem with Checkbox.
   */
  withCheckbox: PropTypes.bool,

  /** Wrap the content if it doesn't fit the width of the container. */
  wrap: PropTypes.bool
} : {};
MenuItemGroup.defaultProps = {
  alwaysRenderChildren: false,
  children: null,
  className: "",
  expandable: false,
  expanded: null,
  isChecked: false,
  isIndeterminate: false,
  label: null,
  menuItemGroupStyles: {},
  name: null,
  onExpandedChange: null,
  onClick: null,
  withCheckbox: false,
  wrap: null,
  leftIcon: null,
  groupChildrenInsideLI: false,
  groupElement: "div",
  groupElementProps: {}
};
MenuItemGroup.displayName = MENUITEMGROUP;
export default MenuItemGroup;
/**
 * Utility component for selectively wrapping the MenuItemGroup list item
 * in a KeyboardNavigatorItem if it contains a checkbox such as in the
 * case of MultiSelect.
 */

const ListWrapper = _ref2 => {
  let {
    checkbox,
    children
  } = _ref2;

  if (checkbox) {
    return _jsx(KeyboardNavigatorItem, {
      children: children
    });
  } else {
    return children;
  }
};